import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, LogOut, Download, Lock, FileText, Mail, Phone, Building2,
  Globe, Sparkles, Link2, ShieldCheck, Copy, Check, Trash2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_SITE_URL = (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/$/, "");
const LOGO = "https://customer-assets.emergentagent.com/job_77e99736-a114-44cf-bdab-fa0372b7c2a2/artifacts/nx8mcx1b_Zaynar%20Systems%20logo.png.png";
const TOKEN_KEY = "zs_owner_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [linkModal, setLinkModal] = useState("");
  const [copied, setCopied] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const loadContracts = useCallback(async (tk) => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`${API}/admin/contracts`, { headers: { Authorization: `Bearer ${tk}` } });
      setContracts(data.contracts || []);
    } catch {
      toast.error("Could not load contracts.");
    } finally { setLoadingList(false); }
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setAuthed(true); loadContracts(token); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); })
      .finally(() => setChecking(false));
  }, [token, loadContracts]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      loadContracts(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed.");
    } finally { setSubmitting(false); }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false); setContracts([]);
  };

  const generateClientLink = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/invites`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const base = PUBLIC_SITE_URL || window.location.origin;
      const url = `${base}/?invite=${data.token}`;
      setCopied(false);
      setLinkModal(url);
    } catch {
      toast.error("Could not generate link.");
    } finally { setGenerating(false); }
  };

  const copyLink = async () => {
    const url = linkModal;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      try {
        const el = document.getElementById("zs-link-input");
        el.removeAttribute("readonly");
        el.focus(); el.select(); el.setSelectionRange(0, 99999);
        ok = document.execCommand("copy");
        el.setAttribute("readonly", "true");
      } catch { ok = false; }
    }
    if (ok) { setCopied(true); toast.success("Link copied to clipboard!"); setTimeout(() => setCopied(false), 2500); }
    else { toast.error("Could not auto-copy. Please select the link and copy it manually."); }
  };

  const deleteContract = async (id) => {
    if (!window.confirm("Delete this contract from history? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/contracts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setContracts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Contract deleted.");
    } catch {
      toast.error("Could not delete contract.");
    } finally { setDeletingId(""); }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] zs-grain px-6">
        <motion.form
          onSubmit={handleLogin}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-[#0d0d0d] border border-[#262626] rounded-2xl p-8 md:p-10 shadow-[0_0_60px_rgba(212,175,55,0.06)]"
          data-testid="owner-login-form"
        >
          <img src={LOGO} alt="Zaynar Systems" className="w-16 h-16 mx-auto object-contain mb-5" />
          <p className="text-center text-xs font-sans uppercase tracking-[0.25em] text-gold">Zaynar Systems</p>
          <h1 className="mt-3 text-center font-serif text-3xl font-light text-white">Staff Access</h1>
          <p className="mt-2 text-center font-sans text-sm text-[#777]">Authorized Zaynar Systems staff only.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 block">Staff ID</label>
              <input data-testid="owner-email-input" type="text" required value={username}
                onChange={(e) => setUsername(e.target.value)} placeholder="Enter your Staff ID" autoComplete="username"
                className="w-full bg-transparent border-b border-[#333] text-white py-2.5 focus:outline-none focus:border-gold transition-colors font-sans placeholder-[#555]" />
            </div>
            <div>
              <label className="text-sm font-sans text-[#A3A3A3] mb-1 block">Password</label>
              <input data-testid="owner-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="w-full bg-transparent border-b border-[#333] text-white py-2.5 focus:outline-none focus:border-gold transition-colors font-sans placeholder-[#555]" />
            </div>
          </div>

          <button type="submit" disabled={submitting} data-testid="owner-login-btn"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-gold text-[#050505] font-sans font-medium py-3.5 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4" /> Sign In</>}
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] zs-grain text-white">
      {linkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-5" data-testid="link-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
            className="w-full max-w-lg bg-[#0d0d0d] border border-[#262626] rounded-2xl p-7 relative">
            <button onClick={() => setLinkModal("")} data-testid="close-link-modal"
              className="absolute top-4 right-4 text-[#777] hover:text-white"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gold/15 flex items-center justify-center"><Link2 className="w-5 h-5 text-gold" /></div>
              <div>
                <h3 className="font-serif text-xl text-white">Client Signing Link</h3>
                <p className="font-sans text-xs text-[#777]">Single-use · expires in 7 days</p>
              </div>
            </div>
            <p className="font-sans text-sm text-[#A3A3A3] mb-3">Copy this link and send it to your client. They can open and sign the contract once.</p>
            <div className="flex items-stretch gap-2">
              <input id="zs-link-input" data-testid="generated-link-input" readOnly value={linkModal}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-[#050505] border border-[#2a2a2a] rounded-lg px-3 py-3 text-sm font-sans text-gold focus:outline-none focus:border-gold select-all" />
              <button onClick={copyLink} data-testid="copy-link-btn"
                className="inline-flex items-center gap-2 bg-gold text-[#050505] font-sans font-medium px-4 rounded-lg hover:bg-gold-light transition-colors whitespace-nowrap">
                {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
              </button>
            </div>
            <p className="mt-3 font-sans text-xs text-[#555]">Tip: tap the link to select it, then copy manually if needed.</p>
          </motion.div>
        </div>
      )}

      <header className="border-b border-[#1a1a1a] sticky top-0 bg-[#050505]/90 backdrop-blur-md z-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Zaynar Systems" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-serif text-lg leading-none">Owner Dashboard</p>
              <p className="font-sans text-[10px] uppercase tracking-widest text-gold mt-1">Zaynar Systems</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={generateClientLink} disabled={generating} data-testid="generate-client-link-btn"
              className="inline-flex items-center gap-2 bg-gold text-[#050505] text-sm font-sans font-medium px-4 py-2 rounded-md hover:bg-gold-light transition-colors disabled:opacity-60">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              <span className="hidden sm:inline">Generate client link</span>
              <span className="sm:hidden">Link</span>
            </button>
            <button onClick={logout} data-testid="owner-logout-btn"
              className="inline-flex items-center gap-2 text-sm font-sans text-[#A3A3A3] hover:text-white px-3 py-2 transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-serif text-3xl font-light">Signed Contracts</h2>
            <p className="font-sans text-sm text-[#777] mt-1">{contracts.length} total · newest first</p>
          </div>
        </div>

        {loadingList ? (
          <div className="py-20 text-center"><Loader2 className="w-7 h-7 text-gold animate-spin mx-auto" /></div>
        ) : contracts.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-[#262626] rounded-xl">
            <FileText className="w-10 h-10 text-[#444] mx-auto mb-3" />
            <p className="font-sans text-[#777]">No signed contracts yet. Share your client link to get started.</p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="contracts-list">
            {contracts.map((c) => (
              <div key={c.id} data-testid={`contract-row-${c.id}`}
                className="bg-[#0d0d0d] border border-[#1f1f1f] rounded-xl p-5 md:p-6 hover:border-[#333] transition-colors">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-serif text-xl text-white">{c.full_name}</h3>
                      <span className="inline-flex items-center gap-1 text-[10px] font-sans uppercase tracking-wider text-gold border border-gold/30 rounded-full px-2 py-0.5">
                        <Globe className="w-3 h-3" /> {(c.language || "en").toUpperCase()}
                      </span>
                      {c.monthly_support && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-sans uppercase tracking-wider text-gold border border-gold/30 rounded-full px-2 py-0.5">
                          <Sparkles className="w-3 h-3" /> Monthly
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm font-sans text-[#A3A3A3]">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#666]" />{c.company_name}</span>
                      <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#666]" />{c.email}</span>
                      <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#666]" />{c.phone}</span>
                    </div>
                    {c.addons && c.addons.length > 0 && (
                      <p className="mt-2 text-xs font-sans text-[#666]">Add-ons: {c.addons.join(", ")}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-sans text-xs text-[#777] whitespace-nowrap">{c.contract_date}</span>
                    <a href={`${API}/contracts/${c.id}/pdf`} target="_blank" rel="noreferrer"
                      data-testid={`download-pdf-${c.id}`}
                      className="inline-flex items-center gap-2 bg-gold text-[#050505] font-sans text-sm font-medium px-4 py-2 rounded-md hover:bg-gold-light transition-colors">
                      <Download className="w-4 h-4" /> PDF
                    </a>
                    <button onClick={() => deleteContract(c.id)} disabled={deletingId === c.id}
                      data-testid={`delete-contract-${c.id}`} title="Delete contract"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#2a2a2a] text-[#888] hover:text-red-400 hover:border-red-500/50 transition-colors disabled:opacity-50">
                      {deletingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-10 text-center font-sans text-xs text-[#555] flex items-center justify-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-gold" /> Each signed contract is also emailed to you in English.
        </p>
      </main>
    </div>
  );
}
